<template>
  <div>
    
    <p class="title mb-1" style="margin-top: 25px">Asset Classes</p>
    <p class="has-text-grey" style="margin-bottom: 20px">Manage and organize your token asset categories</p>

    <div class="box m-5">

      <div class="columns">
        <div class="column">
          <div class="my-4">
            <p class="has-text-grey is-size-7">Add New Asset Class</p>
            <div class="field has-addons mb-1">
              <p class="control is-expanded">
                <input v-model="newAssetClassName" class="input" type="text" placeholder="Enter asset class name" />
              </p>
              <p class="control">
                <button @click="addAssetClass()" class="button is-dark">Add Class</button>
              </p>
            </div>
          </div>

          <div class="mt-6">
            <p class="has-text-grey is-size-7">All Asset Classes</p>
            <div class="table-container">
              <table class="table is-fullwidth">
                <thead>
                  <tr>
                    <th>Class Name</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(assetClass, index) in allAssetClasses" :key="index">
                    <td>
                      <div class="tags has-addons">
                        <span class="tag is-dark">Class</span>
                        <span class="tag" :class="assetClass.active ? 'is-info' : 'is-dark'">{{ assetClass.name }}</span>
                      </div>
                    </td>
                    <td>
                      <span class="tag" :class="assetClass.active ? 'is-info' : 'is-dark'">
                        {{ assetClass.active ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                    <td>
                      <button 
                        v-if="assetClass.active" 
                        @click="deactivateAssetClass(assetClass.name)" 
                        class="button is-secondary"
                        title="Deactivate class"
                      >
                        <i class="ri-delete-bin-line"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="allAssetClasses.length === 0">
                    <td colspan="3" class="has-text-centered">No asset classes found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as util from "@/utilities/util.js";
import Swal from 'sweetalert2';

export default {
  name: "AssetClassesPage",
  components: {},
  data() {
    return {
      newAssetClassName: "",
      assetClasses: [],
      allAssetClasses: [], // Array to store all asset classes with their status
      isAdmin: false,
      account: null,
      nftContract: null,
    };
  },
  async mounted() {
    this.$watch(
      () => {
        return this.$store.state.isConnected;
      },
      (val) => {
        this.isConnected = val;
      }
    );

    await util.reconnect();
    this.$store.commit("changeConnectionStatus", true);
    this.account = await util.getCoinbase();
    this.$store.commit("changeAccount", this.account);

    // Initialize contract
    try {
      // Asegurarse de que window.myWeb3 esté disponible
      if (!window.myWeb3) {
        await util.reconnect();
      }
      
      this.nftContract = new window.myWeb3.eth.Contract(
        util.CMTYNFT_ABI,
        util.NFT_ContractAddress
      );
    } catch (error) {
      console.error('Error initializing contract:', error);
      util.toast.fire({
        icon: "error",
        title: "Error connecting to blockchain"
      });
      return;
    }

    // Check if user has admin role
    try {
      const ADMIN_ROLE = await this.nftContract.methods.ADMIN_ROLE().call();
      this.isAdmin = await this.nftContract.methods.hasRole(ADMIN_ROLE, this.account).call();
    } catch (error) {
      console.error('Error checking admin role:', error);
      this.isAdmin = false;
    }

    if (!this.isAdmin) {
      util.toast.fire({
        icon: "error",
        title: "You don't have permission to access this page",
      });
      this.$router.push("/");
      return;
    }

    // Load asset classes
    await this.loadAssetClasses();
  },
  methods: {
    async loadAssetClasses() {
      try {
        // Get active asset classes
        const activeClasses = await this.nftContract.methods.getAssetClasses().call();
        
        console.log(activeClasses);

        // Create an array of all asset classes with their status
        this.allAssetClasses = [];
        
        // Add active classes
        for (const className of activeClasses) {
          this.allAssetClasses.push({
            name: className,
            active: true
          });
        }
        
        // Store active classes in original array for backward compatibility
        this.assetClasses = activeClasses;
      } catch (error) {
        console.error("Error loading asset classes:", error);
        util.toast.fire({
          icon: "error",
          title: "Error loading asset classes",
        });
      }
    },
    async addAssetClass() {
      if (!this.newAssetClassName) {
        util.toast.fire({
          icon: "error",
          title: "Please enter a class name",
        });
        return;
      }

      let loader = this.$loading.show({});
      try {
        await this.nftContract.methods
          .addAssetClass(this.newAssetClassName)
          .send({ from: this.account })
          .on("receipt", async () => {
            util.toast.fire({
              icon: "success",
              title: "Asset class added successfully",
            });
            this.newAssetClassName = "";
            await this.loadAssetClasses();
            loader.hide();
          })
          .on("error", (error) => {
            console.error("Error adding asset class:", error);
            loader.hide();
            util.toast.fire({
              icon: "error",
              title: "Error adding asset class",
            });
          });
      } catch (error) {
        console.error("Error adding asset class:", error);
        loader.hide();
        util.toast.fire({
          icon: "error",
          title: "Error adding asset class",
        });
      }
    },
    async deactivateAssetClass(className) {
      // Show confirmation dialog
      const result = await Swal.fire({
        title: 'Confirm Deactivation',
        text: `Are you sure you want to deactivate the asset class "${className}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Accept',
        cancelButtonText: 'Cancel',
        allowOutsideClick: false,
        allowEscapeKey: false
      });

      // If user confirms
      if (result.isConfirmed) {
        let loader = this.$loading.show({});
        try {
          await this.nftContract.methods
            .deactivateAssetClass(className)
            .send({ from: this.account })
            .on("receipt", async () => {
              util.toast.fire({
                icon: "success",
                title: "Asset class deactivated successfully",
              });
              
              // Update the local state to mark the class as inactive
              const index = this.allAssetClasses.findIndex(c => c.name === className);
              if (index !== -1) {
                this.allAssetClasses[index].active = false;
              }
              
              // Also reload classes from contract to ensure data consistency
              await this.loadAssetClasses();
              loader.hide();
            })
            .on("error", (error) => {
              console.error("Error deactivating asset class:", error);
              loader.hide();
              util.toast.fire({
                icon: "error",
                title: "Error deactivating asset class",
              });
            });
        } catch (error) {
          console.error("Error deactivating asset class:", error);
          loader.hide();
          util.toast.fire({
            icon: "error",
            title: "Error deactivating asset class",
          });
        }
      }
    },
  },
};
</script>

<style scoped></style>
